import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import StakeLoading from "@/components/StakeLoading";
import NoData from "@/components/NoData";
import LuMatchLeagues from "../LuMatchLeagues";
import LuMatch from "../LuMatch";
import LuDateTabs from "../LuDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import LeagueFilterV7 from "../LeagueFilterV7";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV7 = ({ type = "all" }) => {
  const { colorPrimary, colorSecondary, colorBg, colorText, colorForeground, hasDataConfig } = useCompConfig();
  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
      }
    : undefined;

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds);
    return baseLeaguesWithMatches.filter(({ league }) => idSet.has(league?.id));
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
  };

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-2 py-3"
      style={themeStyle}
    >
      <div className="flex min-h-10 w-full min-w-0 items-stretch gap-2 md:min-h-14">
        <div className="flex min-h-full w-1/2 min-w-0 shrink items-stretch overflow-x-auto overflow-y-hidden md:w-[78%]">
          <LuDateTabs
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            compact
          />
        </div>
        <div className="flex h-full w-1/2 min-w-0 shrink-0 items-stretch justify-end md:w-[22%] md:min-w-[80px]">
          <LeagueFilterV7
            selectedIds={selectedLeagueIds}
            onChange={setSelectedLeagueIds}
            className="h-full"
            themeStyle={themeStyle}
          />
        </div>
      </div>

      {loading && <StakeLoading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="space-y-2 rounded-lg ">
            {filteredLeaguesWithMatches.length === 0 ? (
              <NoData message="Không có trận đấu nào" />
            ) : (
              filteredLeaguesWithMatches.map(({ league, matches }, leagueIndex) => (
                <LuMatchLeagues
                  key={league?.id || `league-${leagueIndex}`}
                  league={league || {}}
                  matches={matches}
                  render={(match) => (
                    match ? <LuMatch key={match.id || `${league?.id || leagueIndex}-${match?.matchTime || Math.random()}`} match={match} /> : null
                  )}
                  showBookmakerSelect={true}
                />
              ))
            )}
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={handleLoadMore}
            loadingComponent={<StakeLoading />}
            hasMore={hasMore}
          />
        </>
      )}
    </div>
  );
};

export default KbvTiLeV7;
